import { searchEntityDomain } from "building-common/buildingContextQueries";
import OpeningUnitNode from "building-common/buildingEntityNodes/openingUnitNode";
import WallStructuralDomainNode from "building-common/buildingEntityNodes/wallStructuralDomainNode";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import { WALL_DOMAIN_OPENING_INTERSECTION } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { DoorUnit } from "building-types/doors/doorTypes";
import { isBuildingEntityHandleOfType, type BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { WallStructuralDomain } from "building-types/structural/structuralDomainTypes";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { searchDomainAround } from "doc/utils/buildingEntityIndex";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import type { Face } from "geom/brep";
import { addAnalyticFace } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

function indexOpeningUnits(
  units: Table<WindowUnit | DoorUnit>,
  wallStructuralDomains: Table<WallStructuralDomain>,
  table: Map<string, Set<OpeningUnitNode>>,
): void {
  for (const unit of Object.values(units)) {
    const openingNode = new OpeningUnitNode(unit);

    searchEntityDomain(BuildingGenerationContext, openingNode.docEntityHandle, searchDomainAround)
      .map((h) => h.entity)
      .filter((h) => isBuildingEntityHandleOfType(h, "WALL_STRUCTURAL_DOMAIN") && !!wallStructuralDomains[h.id])
      .map((h) => new WallStructuralDomainNode(wallStructuralDomains[h.id]))
      .filter((n) => n.plane.coplanarWith(openingNode.frontPlane, openingNode.getWallOpeningDepth()))
      .forEach((n) => {
        const openings = table.get(n.id) ?? new Set<OpeningUnitNode>();
        openings.add(openingNode);
        table.set(n.id, openings);
      });
  }
}

/**
 * Window and door unit handles are not reliably indexed, so we build a mapping from
 * wall structural domain ID to the opening unit nodes that intersect it.
 */
function buildDomainToOpeningsTable(building: ReturnType<typeof select>): Map<string, Set<OpeningUnitNode>> {
  const table = new Map<string, Set<OpeningUnitNode>>();
  const { wallStructuralDomains, windowUnits, doorUnits } = building;
  indexOpeningUnits(windowUnits, wallStructuralDomains, table);
  indexOpeningUnits(doorUnits, wallStructuralDomains, table);
  return table;
}

function addDomainAnalyticEntities(
  wsd: WallStructuralDomain,
  openings: OpeningUnitNode[],
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};

  const wsdNode = new WallStructuralDomainNode(wsd);
  const intersectionFaces: Face[] = openings.flatMap((o) => wsdNode.face.planarOverlapWith(o.frontFace));

  const handle: BuildingEntityHandle = { entityType: "WALL_STRUCTURAL_DOMAIN", id: wsd.id };
  const parents: DocEntityHandle[] = [{ type: "GENERATED_ENTITY", entity: handle }];

  for (const face of intersectionFaces) {
    addAnalyticFace(
      "ANALYTIC_WALL_STRUCTURAL_DOMAIN",
      entities,
      "Wall domain opening intersection",
      parents,
      face,
      undefined,
      [WALL_DOMAIN_OPENING_INTERSECTION],
      {
        color: 0xff8800,
        opacity: percentNew(0.3),
        lineWidth3d: worldInchesNew(0.1),
      },
    );
  }

  return entities;
}

export function addAllWallStructuralDomainAnalyticEntities(
  building: ReturnType<typeof select>,
): Table<BuildingAnalyticEntity> {
  const domainToOpenings = buildDomainToOpeningsTable(building);
  const allEntities: RWTable<BuildingAnalyticEntity> = {};

  for (const wsd of Object.values(building.wallStructuralDomains)) {
    const openings = Array.from(domainToOpenings.get(wsd.id) ?? []);
    Object.assign(allEntities, addDomainAnalyticEntities(wsd, openings));
  }

  return allEntities;
}

const select = makeBuildingSelector(["wallStructuralDomains", "doorUnits", "windowUnits"]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticWallStructuralDomainEntities"> {
  return { analyticWallStructuralDomainEntities: addAllWallStructuralDomainAnalyticEntities(building) };
}

export default memo(select, generate);
